import React from 'react';
import { Link } from 'react-router-dom';
import { Home, Search, Music, Disc3 } from 'lucide-react';
import { useSearch } from '../context/SearchContext';
import './NotFound.css';

interface NotFoundProps {
  type?: 'naat' | 'naatkhawan' | 'page';
  message?: string;
}

const NotFound: React.FC<NotFoundProps> = ({
  type = 'page',
  message = "Oops! You've reached a page that doesn't exist yet."
}) => {
  const { openSearch } = useSearch();

  const getTitle = () => {
    switch(type) {
      case 'naat':
        return 'Naat Not Found';
      case 'naatkhawan':
        return 'Naat Khawan Not Found';
      default:
        return 'Page Not Found';
    }
  };

  const getDescription = () => {
    switch(type) {
      case 'naat':
        return "The naat you're looking for might have been moved or doesn't exist. Let's help you find what you're looking for.";
      case 'naatkhawan':
        return "This Naat Khawan's page couldn't be found. Perhaps you'd like to explore other talented reciters?";
      default:
        return "The page you're looking for seems to have wandered off. Don't worry, we'll help you get back on track!";
    }
  };

  return (
    <div className="not-found-container">
      <div className="not-found-content">
        {/* Animated Graphics */}
        <div className="not-found-animation">
          <div className="broken-disc">
            <Disc3 size={100} className="disc-icon" />
            <div className="disc-crack"></div>
          </div>
          <div className="music-notes">
            <span className="note note-1">♪</span>
            <span className="note note-2">♫</span>
            <span className="note note-3">♬</span>
            <span className="note note-4">♪</span>
          </div>
        </div>

        {/* Error Code */}
        <div className="error-code">404</div>

        {/* Title and Message */}
        <h1 className="not-found-title">{getTitle()}</h1>
        <p className="not-found-message">{message}</p>
        <p className="not-found-description">{getDescription()}</p>

        {/* Action Buttons */}
        <div className="not-found-actions">
          <Link to="/" className="not-found-btn not-found-btn-primary">
            <Home size={16} />
            Homepage
          </Link>

          <button
            onClick={openSearch}
            className="not-found-btn not-found-btn-accent"
          >
            <Search size={16} />
            Search
          </button>
        </div>

        {/* Suggestions Section - More Compact */}
        <div className="not-found-suggestions">
          <h3>You might want to:</h3>
          <div className="suggestion-links">
            <Link to="/naatkhawans" className="suggestion-link">
              <Music size={14} />
              Browse Naat Khawans
            </Link>
            <Link to="/popular" className="suggestion-link">
              <Music size={14} />
              Popular Naats
            </Link>
            <Link to="/recent" className="suggestion-link">
              <Music size={14} />
              Recent Naats
            </Link>
          </div>
        </div>

        {/* Decorative Elements */}
        <div className="floating-shapes">
          <div className="shape shape-1"></div>
          <div className="shape shape-2"></div>
          <div className="shape shape-3"></div>
        </div>
      </div>
    </div>
  );
};

export default NotFound;